// This file is auto-generated by @hey-api/openapi-ts

/**
 * Serializer for the comprehensive Address model
 */
export type Address = {
  readonly id: number;
  /**
   * Street address, apartment, suite, etc.
   */
  street_address?: string;
  city?: string;
  /**
   * State, province, or region
   */
  state_province?: string;
  /**
   * ZIP code, postal code, etc.
   */
  postal_code?: string;
  country?: string;
  /**
   * Latitude for geocoding and distance calculations
   */
  latitude?: string | null;
  /**
   * Longitude for geocoding and distance calculations
   */
  longitude?: string | null;
  /**
   * User prefers remote work over this location
   */
  is_remote_preferred?: boolean;
  /**
   * Address has been verified through geocoding
   */
  is_verified?: boolean;
  /**
   * Full formatted address string for display
   */
  readonly formatted_address: string;
  readonly created: string;
  readonly updated: string;
};

/**
 * Serializer for address data in search results
 */
export type AddressSearch = {
  city: string | null;
  state: string | null;
  country: string | null;
  formatted_address: string | null;
};

export type Application = {
  readonly id: number;
  readonly experiences: Array<Experience>;
  readonly educations: Array<Education>;
  readonly skills: Array<ApplicationSkill>;
  readonly projects: Array<Project>;
  readonly awards: Array<Award>;
  readonly certifications: Array<Certification>;
  readonly publications: Array<Publication>;
  readonly profiles: Array<Profile>;
  linkedin?: string;
  github?: string;
  portfolio?: string;
  description?: string;
  /**
   * Professional summary or objective
   */
  summary?: string;
  readonly created: string;
  readonly updated: string;
  user: number;
};

export type ApplicationID = {
  readonly id: number;
};

/**
 * Serializer for application data in search results
 */
export type ApplicationSearch = {
  description: string | null;
  summary: string | null;
  linkedin: string | null;
  github: string | null;
  portfolio: string | null;
};

export type ApplicationSkill = {
  readonly id: number;
  name: string;
  score: number;
  application: number;
  readonly matching_skill: MatchingSkill;
  matching_skill_id?: number | null;
};

export type Award = {
  readonly id: number;
  name: string;
  description?: string;
  issuer?: string;
  date_received?: string | null;
  application: number;
};

export type BlankEnum = "";

/**
 * * `owner` - Owner/Founder
 * * `ceo` - CEO/President
 * * `gm` - General Manager
 * * `marketing` - Marketing Manager
 * * `operations` - Operations Manager
 * * `hr` - HR Manager
 * * `finance` - Finance Manager
 * * `sales` - Sales Manager
 * * `it` - IT Manager
 * * `customer_service` - Customer Service Manager
 * * `consultant` - Consultant
 * * `recruiter` - Recruiter
 * * `other` - Other
 */
export type BusinessRoleEnum =
  | "owner"
  | "ceo"
  | "gm"
  | "marketing"
  | "operations"
  | "hr"
  | "finance"
  | "sales"
  | "it"
  | "customer_service"
  | "consultant"
  | "recruiter"
  | "other";

/**
 * Serializer for candidate job preferences
 */
export type CandidatePreferences = {
  readonly id: number;
  /**
   * The candidate these preferences belong to
   */
  readonly user: number;
  /**
   * Preferred job role or position
   */
  role?: string;
  /**
   * List of preferred locations
   */
  locations?: unknown;
  /**
   * Preferred opportunity type
   *
   * * `All` - All
   * * `Full Time` - Full Time
   * * `Part Time` - Part Time
   * * `Volunteer` - Volunteer
   * * `AI gig` - AI gig
   */
  opportunity_type?: OpportunityTypeEnum;
  /**
   * Preferred salary range
   *
   * * `10K - 100K` - 10K - 100K
   * * `100K - 200K` - 100K - 200K
   * * `200K - 300K` - 200K - 300K
   * * `More than 300K` - More than 300K
   */
  preferred_salary?: PreferredSalaryEnum | BlankEnum;
  job_types?: Array<JobTypesEnum>;
  company_sizes?: Array<CompanySizesEnum>;
  investment_stages?: Array<InvestmentStagesEnum>;
  /**
   * Whether candidate is open to remote work
   */
  open_to_remote?: boolean;
  /**
   * Whether candidate requires visa sponsorship
   */
  requires_visa_sponsorship?: boolean;
  readonly created: string;
  readonly updated: string;
};

/**
 * Serializer for public candidate profile data accessible by recruiters
 */
export type CandidatePublicProfile = {
  readonly id: number;
  name: string;
  profile_picture?: string | null;
  readonly address_details: Address;
  readonly role: RoleEnum;
  /**
   * Check if this candidate is available as a volunteer
   */
  is_volunteer?: boolean;
  /**
   * Check if this candidate is actively looking for a job
   */
  is_looking_for_job?: boolean;
  readonly applicationids: Array<ApplicationID>;
};

/**
 * Serializer for candidate search results
 */
export type CandidateSearch = {
  id: number;
  name: string;
  email: string;
  role: string;
  is_looking_for_job: boolean;
  address: AddressSearch | null;
  application: ApplicationSearch | null;
  skills: Array<SkillSearch>;
  experiences: Array<ExperienceSearch>;
  education: Array<EducationSearch>;
  profile_picture: string | null;
  search_rank: number;
  match_score: number;
  total_score: number;
};

/**
 * * `feature` - Feature
 * * `fix` - Fix
 * * `chore` - Chore
 */
export type CategoryEnum = "feature" | "fix" | "chore";

export type Certification = {
  readonly id: number;
  name: string;
  description?: string;
  issuer?: string;
  issue_date?: string | null;
  expiry_date?: string | null;
  credential_id?: string;
  application: number;
};

export type ChangeLogEntry = {
  readonly id: number;
  title: string;
  /**
   * Brief explanation of the change.
   */
  description?: string;
  category?: CategoryEnum;
  pr_number: number;
  readonly pr_url: string;
  /**
   * Comma-separated issue numbers, e.g. '#174, #175'.
   */
  task_refs?: string;
  /**
   * When the PR merged.
   */
  occurred_at: string;
};

export type ChapterLeaderboard = {
  rank: number;
  id: number;
  name: string;
  slug: string;
  tier: string;
  tier_display: string;
  score: number;
};

export type Company = {
  readonly id: number;
  readonly smallbiz_profile: SmallBusinessProfile;
  name: string;
  address: string;
  description?: string;
  website?: string;
  logo?: string | null;
  contact_email?: string;
  phone_number?: string;
  /**
   * Check if this is a small business (listed on volunteer portal)
   */
  is_small_business?: boolean;
  /**
   * Check if this is a nonprofit organization
   */
  is_nonprofit?: boolean;
  industry?: IndustryEnum | BlankEnum;
  employee_count?: EmployeeCountEnum | BlankEnum;
  /**
   * Describe the company culture, values, and work environment
   */
  culture?: string;
  /**
   * Describe the company's hiring goals and objectives
   */
  hiring_goals?: string;
  /**
   * Types of roles the company is currently hiring for
   */
  roles_hiring_for?: string;
  /**
   * Number of positions the company plans to hire
   */
  positions_to_hire?: number | null;
};

/**
 * * `1-10 employees` - 1-10 employees
 * * `11-50 employees` - 11-50 employees
 * * `51-200 employees` - 51-200 employees
 * * `201-500 employees` - 201-500 employees
 * * `501-1000 employees` - 501-1000 employees
 * * `1001-5000 employees` - 1001-5000 employees
 * * `5000+ employees` - 5000+ employees
 */
export type CompanySizesEnum =
  | "1-10 employees"
  | "11-50 employees"
  | "51-200 employees"
  | "201-500 employees"
  | "501-1000 employees"
  | "1001-5000 employees"
  | "5000+ employees";

export type DeveloperLeaderboard = {
  rank: number;
  id: number;
  name: string;
  score: number;
};

/**
 * * `1` - 1
 * * `2` - 2
 * * `3` - 3
 * * `4` - 4
 * * `5` - 5
 */
export type DigitalProficiencyEnum = 1 | 2 | 3 | 4 | 5;

export type Education = {
  readonly id: number;
  institution: string;
  degree?: string;
  field_of_study?: string;
  /**
   * Grade Point Average (0.00-4.00)
   */
  gpa?: string | null;
  start_date: string;
  end_date?: string | null;
  application: number;
};

/**
 * Serializer for education in search results
 */
export type EducationSearch = {
  institution: string;
  degree: string;
  field_of_study: string;
};

/**
 * * `sole` - Just myself (Sole Proprietor)
 * * `2_5` - 2-5
 * * `6_15` - 6-15
 * * `16_30` - 16-30
 * * `31_50` - 31-50
 * * `50_plus` - More than 50
 */
export type EmployeeCountEnum =
  "sole" | "2_5" | "6_15" | "16_30" | "31_50" | "50_plus";

export type Event = {
  readonly id: number;
  title: string;
  description: string;
  event_type?: EventTypeEnum;
  start_date: string;
  end_date: string;
  location: string;
  city: string;
  country: string;
  is_online?: boolean;
  online_url?: string;
  max_participants?: number | null;
  readonly current_participants: number;
  is_active?: boolean;
  readonly tags: Array<Tag>;
  tag_ids?: Array<number>;
  organizer_name: string;
  organizer_email: string;
  organizer_phone?: string;
  registration_required?: boolean;
  registration_deadline?: string | null;
  cost?: string | null;
  currency?: string;
  readonly is_full: boolean;
  readonly registration_open: boolean;
  readonly created: string;
  readonly modified: string;
};

export type EventList = {
  readonly id: number;
  title: string;
  event_type?: EventTypeEnum;
  start_date: string;
  end_date: string;
  location: string;
  city: string;
  country: string;
  is_online?: boolean;
  is_active?: boolean;
  readonly tags: Array<Tag>;
  readonly is_full: boolean;
  readonly registration_open: boolean;
};

/**
 * * `workshop` - Workshop
 * * `seminar` - Seminar
 * * `conference` - Conference
 * * `networking` - Networking
 * * `training` - Training
 * * `webinar` - Webinar
 * * `other` - Other
 */
export type EventTypeEnum =
  | "workshop"
  | "seminar"
  | "conference"
  | "networking"
  | "training"
  | "webinar"
  | "other";

export type Experience = {
  readonly id: number;
  company: string;
  title: string;
  city?: string;
  state?: string;
  country?: string;
  is_remote?: boolean;
  start_date: string;
  end_date?: string | null;
  responsibilities: string;
  application: number;
};

/**
 * Serializer for experiences in search results
 */
export type ExperienceSearch = {
  company: string;
  title: string;
  responsibilities: string;
};

export type FreeAgent = {
  readonly id: number;
  readonly name: string;
  /**
   * Whether the developer is open to team invites.
   */
  is_free_agent?: boolean;
  /**
   * Short pitch shown in the hub.
   */
  headline?: string;
};

export type GoogleAuth = {
  token: string;
};

/**
 * * `retail` - Retail
 * * `food_beverage` - Food & Beverage
 * * `professional_services` - Professional Services
 * * `healthcare` - Healthcare/Wellness
 * * `trades` - Trades/Services
 * * `ecommerce` - E-commerce/Online Business
 * * `creative` - Creative/Design
 * * `nonprofit` - Non-profit
 * * `other` - Other
 */
export type IndustryEnum =
  | "retail"
  | "food_beverage"
  | "professional_services"
  | "healthcare"
  | "trades"
  | "ecommerce"
  | "creative"
  | "nonprofit"
  | "other";

/**
 * * `NA` - NA
 * * `Seed` - Seed
 * * `Series A` - Series A
 * * `Series B` - Series B
 * * `Growth` - Growth
 * * `IPO` - IPO
 * * `Acquired` - Acquired
 */
export type InvestmentStagesEnum =
  "NA" | "Seed" | "Series A" | "Series B" | "Growth" | "IPO" | "Acquired";

export type JobProfile = {
  readonly id: number;
  title: string;
  description: string;
  keywords: string;
  salary_min: string;
  salary_max: string;
  city: string;
  state: string;
  country: string;
  latitude?: string | null;
  longitude?: string | null;
  is_remote?: boolean;
  job_type?: JobTypeEnum;
  /**
   * Required educational qualifications
   */
  degree_requirements: string;
  /**
   * Required years of experience
   */
  years_of_experience: number;
  /**
   * List of benefits and perks offered
   */
  benefits_and_perks: string;
  visa_sponsor?: boolean;
  created_by: number;
  company: number;
  readonly company_name: string;
  is_active?: boolean;
  readonly created_at: string;
  readonly updated_at: string;
};

export type JobSkillMatrix = {
  readonly id: number;
  job: number;
  readonly job_title: string;
  readonly company_name: string;
  /**
   * Required skill levels and weights in format: {skill_id: {'required_level': 0-10, 'weight': 0-1}}
   */
  skills_data?: unknown;
  readonly last_updated: string;
  is_active?: boolean;
};

/**
 * * `full-time` - Full Time
 * * `intern` - Intern
 * * `volunteering` - Volunteering
 */
export type JobTypeEnum = "full-time" | "intern" | "volunteering";

/**
 * * `Full Time` - Full Time
 * * `Contract` - Contract
 * * `Internship` - Internship
 * * `Co-Founder` - Co-Founder
 */
export type JobTypesEnum =
  "Full Time" | "Contract" | "Internship" | "Co-Founder";

export type Login = {
  email: string;
  password: string;
};

export type MatchResult = {
  readonly id: number;
  readonly user: number;
  readonly user_name: string;
  readonly user_email: string;
  readonly job: number | null;
  readonly job_title: string;
  readonly job_company: string;
  readonly volunteer_opportunity: number | null;
  readonly volunteer_title: string;
  readonly volunteer_company: string;
  readonly match_type: MatchTypeEnum;
  status?: MatchResultStatusEnum;
  readonly confidence_score: number;
  /**
   * Skill gaps between user and job/opportunity
   */
  readonly skill_gaps: unknown;
  readonly algorithm_version: string;
  /**
   * Additional algorithm metadata
   */
  readonly match_metadata: unknown;
  /**
   * User saved this match for later review
   */
  is_saved?: boolean;
  /**
   * User marked this as a good fit
   */
  is_good_fit?: boolean;
  /**
   * User marked this as not suitable
   */
  is_not_for_me?: boolean;
  readonly created_at: string;
  readonly updated_at: string;
  expires_at?: string | null;
};

/**
 * * `pending` - Pending
 * * `matched` - Matched
 * * `rejected` - Rejected
 * * `expired` - Expired
 */
export type MatchResultStatusEnum =
  "pending" | "matched" | "rejected" | "expired";

/**
 * * `job` - Job Match
 * * `volunteer` - Volunteer Match
 */
export type MatchTypeEnum = "job" | "volunteer";

export type MatchingSession = {
  readonly id: number;
  readonly session_id: string;
  user: number;
  readonly user_name: string;
  match_type: MatchTypeEnum;
  /**
   * Filters applied during matching
   */
  filters_applied?: unknown;
  results_count?: number;
  readonly created_at: string;
  completed_at?: string | null;
};

/**
 * Serializer for the matching skill reference
 */
export type MatchingSkill = {
  id: number;
  name: string;
  category: string | null;
};

export type Message = {
  message: string;
};

export type NonProfitProfile = {
  readonly id: number;
  /**
   * Mission statement of the nonprofit
   */
  mission_statement?: string;
  /**
   * Programs or services offered by the nonprofit
   */
  programs?: string;
  /**
   * Available volunteer opportunities within the nonprofit
   */
  volunteer_opportunities?: string;
  company: number;
};

export type NullEnum = unknown;

/**
 * * `All` - All
 * * `Full Time` - Full Time
 * * `Part Time` - Part Time
 * * `Volunteer` - Volunteer
 * * `AI gig` - AI gig
 */
export type OpportunityTypeEnum =
  "All" | "Full Time" | "Part Time" | "Volunteer" | "AI gig";

export type PaginatedApplicationList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Application>;
};

export type PaginatedApplicationSkillList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<ApplicationSkill>;
};

export type PaginatedAwardList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Award>;
};

export type PaginatedCertificationList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Certification>;
};

export type PaginatedChangeLogEntryList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<ChangeLogEntry>;
};

export type PaginatedCompanyList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Company>;
};

export type PaginatedEducationList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Education>;
};

export type PaginatedEventListList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<EventList>;
};

export type PaginatedExperienceList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Experience>;
};

export type PaginatedJobProfileList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<JobProfile>;
};

export type PaginatedJobSkillMatrixList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<JobSkillMatrix>;
};

export type PaginatedMatchResultList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<MatchResult>;
};

export type PaginatedMatchingSessionList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<MatchingSession>;
};

export type PaginatedNonProfitProfileList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<NonProfitProfile>;
};

export type PaginatedProfileList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Profile>;
};

export type PaginatedProjectList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Project>;
};

export type PaginatedPublicationList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Publication>;
};

export type PaginatedResourceListList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<ResourceList>;
};

export type PaginatedSkillCategoryList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<SkillCategory>;
};

export type PaginatedSkillList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Skill>;
};

export type PaginatedSmallBusinessProfileList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<SmallBusinessProfile>;
};

export type PaginatedTagList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<Tag>;
};

export type PaginatedUserList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<User>;
};

export type PaginatedUserSkillMatrixList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<UserSkillMatrix>;
};

export type PaginatedVolunteerOpportunityList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<VolunteerOpportunity>;
};

export type PatchedApplication = {
  readonly id?: number;
  readonly experiences?: Array<Experience>;
  readonly educations?: Array<Education>;
  readonly skills?: Array<ApplicationSkill>;
  readonly projects?: Array<Project>;
  readonly awards?: Array<Award>;
  readonly certifications?: Array<Certification>;
  readonly publications?: Array<Publication>;
  readonly profiles?: Array<Profile>;
  linkedin?: string;
  github?: string;
  portfolio?: string;
  description?: string;
  /**
   * Professional summary or objective
   */
  summary?: string;
  readonly created?: string;
  readonly updated?: string;
  user?: number;
};

export type PatchedApplicationSkill = {
  readonly id?: number;
  name?: string;
  score?: number;
  application?: number;
  readonly matching_skill?: MatchingSkill;
  matching_skill_id?: number | null;
};

export type PatchedAward = {
  readonly id?: number;
  name?: string;
  description?: string;
  issuer?: string;
  date_received?: string | null;
  application?: number;
};

export type PatchedCertification = {
  readonly id?: number;
  name?: string;
  description?: string;
  issuer?: string;
  issue_date?: string | null;
  expiry_date?: string | null;
  credential_id?: string;
  application?: number;
};

export type PatchedCompany = {
  readonly id?: number;
  readonly smallbiz_profile?: SmallBusinessProfile;
  name?: string;
  address?: string;
  description?: string;
  website?: string;
  logo?: string | null;
  contact_email?: string;
  phone_number?: string;
  /**
   * Check if this is a small business (listed on volunteer portal)
   */
  is_small_business?: boolean;
  /**
   * Check if this is a nonprofit organization
   */
  is_nonprofit?: boolean;
  industry?: IndustryEnum | BlankEnum;
  employee_count?: EmployeeCountEnum | BlankEnum;
  /**
   * Describe the company culture, values, and work environment
   */
  culture?: string;
  /**
   * Describe the company's hiring goals and objectives
   */
  hiring_goals?: string;
  /**
   * Types of roles the company is currently hiring for
   */
  roles_hiring_for?: string;
  /**
   * Number of positions the company plans to hire
   */
  positions_to_hire?: number | null;
};

export type PatchedEducation = {
  readonly id?: number;
  institution?: string;
  degree?: string;
  field_of_study?: string;
  /**
   * Grade Point Average (0.00-4.00)
   */
  gpa?: string | null;
  start_date?: string;
  end_date?: string | null;
  application?: number;
};

export type PatchedEvent = {
  readonly id?: number;
  title?: string;
  description?: string;
  event_type?: EventTypeEnum;
  start_date?: string;
  end_date?: string;
  location?: string;
  city?: string;
  country?: string;
  is_online?: boolean;
  online_url?: string;
  max_participants?: number | null;
  readonly current_participants?: number;
  is_active?: boolean;
  readonly tags?: Array<Tag>;
  tag_ids?: Array<number>;
  organizer_name?: string;
  organizer_email?: string;
  organizer_phone?: string;
  registration_required?: boolean;
  registration_deadline?: string | null;
  cost?: string | null;
  currency?: string;
  readonly is_full?: boolean;
  readonly registration_open?: boolean;
  readonly created?: string;
  readonly modified?: string;
};

export type PatchedExperience = {
  readonly id?: number;
  company?: string;
  title?: string;
  city?: string;
  state?: string;
  country?: string;
  is_remote?: boolean;
  start_date?: string;
  end_date?: string | null;
  responsibilities?: string;
  application?: number;
};

export type PatchedJobProfile = {
  readonly id?: number;
  title?: string;
  description?: string;
  keywords?: string;
  salary_min?: string;
  salary_max?: string;
  city?: string;
  state?: string;
  country?: string;
  latitude?: string | null;
  longitude?: string | null;
  is_remote?: boolean;
  job_type?: JobTypeEnum;
  /**
   * Required educational qualifications
   */
  degree_requirements?: string;
  /**
   * Required years of experience
   */
  years_of_experience?: number;
  /**
   * List of benefits and perks offered
   */
  benefits_and_perks?: string;
  visa_sponsor?: boolean;
  created_by?: number;
  company?: number;
  readonly company_name?: string;
  is_active?: boolean;
  readonly created_at?: string;
  readonly updated_at?: string;
};

export type PatchedJobSkillMatrix = {
  readonly id?: number;
  job?: number;
  readonly job_title?: string;
  readonly company_name?: string;
  /**
   * Required skill levels and weights in format: {skill_id: {'required_level': 0-10, 'weight': 0-1}}
   */
  skills_data?: unknown;
  readonly last_updated?: string;
  is_active?: boolean;
};

export type PatchedMatchResult = {
  readonly id?: number;
  readonly user?: number;
  readonly user_name?: string;
  readonly user_email?: string;
  readonly job?: number | null;
  readonly job_title?: string;
  readonly job_company?: string;
  readonly volunteer_opportunity?: number | null;
  readonly volunteer_title?: string;
  readonly volunteer_company?: string;
  readonly match_type?: MatchTypeEnum;
  status?: MatchResultStatusEnum;
  readonly confidence_score?: number;
  /**
   * Skill gaps between user and job/opportunity
   */
  readonly skill_gaps?: unknown;
  readonly algorithm_version?: string;
  /**
   * Additional algorithm metadata
   */
  readonly match_metadata?: unknown;
  /**
   * User saved this match for later review
   */
  is_saved?: boolean;
  /**
   * User marked this as a good fit
   */
  is_good_fit?: boolean;
  /**
   * User marked this as not suitable
   */
  is_not_for_me?: boolean;
  readonly created_at?: string;
  readonly updated_at?: string;
  expires_at?: string | null;
};

export type PatchedNonProfitProfile = {
  readonly id?: number;
  /**
   * Mission statement of the nonprofit
   */
  mission_statement?: string;
  /**
   * Programs or services offered by the nonprofit
   */
  programs?: string;
  /**
   * Available volunteer opportunities within the nonprofit
   */
  volunteer_opportunities?: string;
  company?: number;
};

export type PatchedProfile = {
  readonly id?: number;
  platform?: PlatformEnum;
  username?: string;
  profile_url?: string;
  rating?: number | null;
  solved_problems?: number | null;
  application?: number;
};

export type PatchedProject = {
  readonly id?: number;
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string | null;
  link?: string;
  application?: number;
};

export type PatchedPublication = {
  readonly id?: number;
  title?: string;
  authors?: string;
  journal_or_conference?: string;
  publication_date?: string | null;
  doi?: string;
  link?: string;
  application?: number;
};

export type PatchedResource = {
  readonly id?: number;
  title?: string;
  description?: string;
  resource_type?: ResourceTypeEnum;
  url?: string;
  file?: string | null;
  author?: string;
  publisher?: string;
  published_date?: string | null;
  language?: string;
  is_free?: boolean;
  cost?: string | null;
  currency?: string;
  location?: string;
  city?: string;
  country?: string;
  is_active?: boolean;
  readonly tags?: Array<Tag>;
  tag_ids?: Array<number>;
  readonly views_count?: number;
  readonly download_count?: number;
  readonly created?: string;
  readonly modified?: string;
};

export type PatchedSmallBusinessProfile = {
  readonly id?: number;
  company?: number;
  readonly company_name?: string;
  readonly company_address?: string;
  years_operating?: YearsOperatingEnum | BlankEnum;
  /**
   * 1 = Very Basic, 5 = Highly Advanced
   *
   * * `1` - 1
   * * `2` - 2
   * * `3` - 3
   * * `4` - 4
   * * `5` - 5
   */
  digital_proficiency?: DigitalProficiencyEnum | NullEnum | null;
  /**
   * Is this business currently seeking volunteers?
   */
  seeking_volunteers?: boolean;
  /**
   * Describe the main problems or challenges the business is facing
   */
  problems_facing?: string;
  /**
   * Describe volunteer roles or needs
   */
  volunteer_roles?: string;
  /**
   * Opt-in to receive information about volunteer events and opportunities
   */
  opt_in_for_events?: boolean;
};

export type PatchedSubmission = {
  readonly id?: number;
  team?: number;
  readonly team_name?: string;
  hackathon?: number;
  readonly hackathon_name?: string;
  /**
   * Link to the team's code repository.
   */
  repo_url?: string;
  /**
   * Optional demo video link.
   */
  video_url?: string;
  readonly status?: SubmissionStatusEnum;
  /**
   * Why the submission failed validation, if it did.
   */
  readonly validation_notes?: string;
  /**
   * Immutable roster captured at lock time.
   */
  readonly roster_snapshot?: unknown;
  readonly is_locked?: boolean;
  readonly locked_at?: string | null;
  readonly updated_at?: string;
};

export type PatchedUser = {
  readonly id?: number;
  email?: string;
  name?: string;
  phone_number?: string;
  profile_picture?: string | null;
  resume?: string | null;
  /**
   * User's primary address for job matching and location-based features
   */
  address?: number | null;
  readonly address_details?: Address;
  address_data?: Address;
  /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean;
  /**
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean;
  /**
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean;
  created?: string;
  readonly modified?: string;
  last_login?: string | null;
  role?: RoleEnum;
  /**
   * Specific role within the small business (if applicable)
   *
   * * `owner` - Owner/Founder
   * * `ceo` - CEO/President
   * * `gm` - General Manager
   * * `marketing` - Marketing Manager
   * * `operations` - Operations Manager
   * * `hr` - HR Manager
   * * `finance` - Finance Manager
   * * `sales` - Sales Manager
   * * `it` - IT Manager
   * * `customer_service` - Customer Service Manager
   * * `consultant` - Consultant
   * * `recruiter` - Recruiter
   * * `other` - Other
   */
  business_role?: BusinessRoleEnum | BlankEnum;
  /**
   * Check if this candidate is available as a volunteer
   */
  is_volunteer?: boolean;
  /**
   * Check if this candidate is actively looking for a job
   */
  is_looking_for_job?: boolean;
  company?: number | null;
  readonly applicationids?: Array<ApplicationID>;
};

export type PatchedUserSkillMatrix = {
  readonly id?: number;
  readonly user?: number;
  readonly user_name?: string;
  readonly user_email?: string;
  /**
   * Skill scores and preferences in format: {skill_id: {'score': 0-10, 'preference': 0-10}}
   */
  skills_data?: unknown;
  readonly last_updated?: string;
  is_active?: boolean;
};

export type PatchedVolunteerOpportunity = {
  readonly id?: number;
  company?: number;
  readonly company_name?: string;
  readonly company_type?: string;
  title?: string;
  description?: string;
  /**
   * Required skill levels and weights
   */
  skills_data?: unknown;
  is_active?: boolean;
  readonly created_at?: string;
  readonly updated_at?: string;
};

/**
 * * `leetcode` - LeetCode
 * * `codeforces` - Codeforces
 * * `codechef` - CodeChef
 * * `hackerrank` - HackerRank
 * * `github` - GitHub
 * * `linkedin` - LinkedIn
 * * `other` - Other
 */
export type PlatformEnum =
  | "leetcode"
  | "codeforces"
  | "codechef"
  | "hackerrank"
  | "github"
  | "linkedin"
  | "other";

/**
 * * `10K - 100K` - 10K - 100K
 * * `100K - 200K` - 100K - 200K
 * * `200K - 300K` - 200K - 300K
 * * `More than 300K` - More than 300K
 */
export type PreferredSalaryEnum =
  "10K - 100K" | "100K - 200K" | "200K - 300K" | "More than 300K";

export type Profile = {
  readonly id: number;
  platform: PlatformEnum;
  username: string;
  profile_url?: string;
  rating?: number | null;
  solved_problems?: number | null;
  application: number;
};

export type Project = {
  readonly id: number;
  name: string;
  description: string;
  start_date: string;
  end_date?: string | null;
  link?: string;
  application: number;
};

export type Publication = {
  readonly id: number;
  title: string;
  authors?: string;
  journal_or_conference?: string;
  publication_date?: string | null;
  doi?: string;
  link?: string;
  application: number;
};

export type Resource = {
  readonly id: number;
  title: string;
  description: string;
  resource_type?: ResourceTypeEnum;
  url?: string;
  file?: string | null;
  author: string;
  publisher?: string;
  published_date?: string | null;
  language?: string;
  is_free?: boolean;
  cost?: string | null;
  currency?: string;
  location?: string;
  city?: string;
  country?: string;
  is_active?: boolean;
  readonly tags: Array<Tag>;
  tag_ids?: Array<number>;
  readonly views_count: number;
  readonly download_count: number;
  readonly created: string;
  readonly modified: string;
};

export type ResourceList = {
  readonly id: number;
  title: string;
  resource_type?: ResourceTypeEnum;
  author: string;
  is_free?: boolean;
  location?: string;
  city?: string;
  country?: string;
  is_active?: boolean;
  readonly tags: Array<Tag>;
  views_count?: number;
  download_count?: number;
  published_date?: string | null;
};

/**
 * * `article` - Article
 * * `video` - Video
 * * `podcast` - Podcast
 * * `ebook` - E-Book
 * * `template` - Template
 * * `tool` - Tool
 * * `course` - Course
 * * `guide` - Guide
 * * `other` - Other
 */
export type ResourceTypeEnum =
  | "article"
  | "video"
  | "podcast"
  | "ebook"
  | "template"
  | "tool"
  | "course"
  | "guide"
  | "other";

/**
 * * `admin` - Admin
 * * `staff` - Staff
 * * `candidate` - Candidate
 * * `recruiter` - Recruiter
 * * `smallbiz_owner` - Small Business Owner
 * * `nonprofit` - Nonprofit Organization
 */
export type RoleEnum =
  | "admin"
  | "staff"
  | "candidate"
  | "recruiter"
  | "smallbiz_owner"
  | "nonprofit";

/**
 * Serializer for search request
 */
export type SearchRequest = {
  /**
   * Natural language search query (e.g., 'backend engineer with 5 years experience around DC')
   */
  query: string;
  /**
   * Additional filters (location, experience_level, etc.)
   */
  filters?: {
    [key: string]: unknown;
  };
  /**
   * Maximum number of results to return
   */
  limit?: number;
};

/**
 * Serializer for search response
 */
export type SearchResponse = {
  results: Array<CandidateSearch>;
  total_count: number;
  query: string;
  filters_applied: {
    [key: string]: unknown;
  };
  search_time_ms: number;
};

export type SendEmail = {
  subject: string;
  body: string;
  from_email: string;
  to_email: string;
};

export type Session = {
  isauthenticated: boolean;
};

export type Signup = {
  email: string;
  password: string;
  name: string;
  phone_number?: string;
  address_data?: Address;
  role?: RoleEnum;
};

export type Skill = {
  readonly id: number;
  name: string;
  readonly category: SkillCategory;
  /**
   * Alternative names for this skill
   */
  synonyms?: unknown;
  description?: string;
  is_active?: boolean;
};

export type SkillCategory = {
  readonly id: number;
  name: string;
  description?: string;
  parent?: number | null;
  is_active?: boolean;
};

/**
 * Serializer for skills in search results
 */
export type SkillSearch = {
  name: string;
  score: number;
};

export type SmallBusinessProfile = {
  readonly id: number;
  company: number;
  readonly company_name: string;
  readonly company_address: string;
  years_operating?: YearsOperatingEnum | BlankEnum;
  /**
   * 1 = Very Basic, 5 = Highly Advanced
   *
   * * `1` - 1
   * * `2` - 2
   * * `3` - 3
   * * `4` - 4
   * * `5` - 5
   */
  digital_proficiency?: DigitalProficiencyEnum | NullEnum | null;
  /**
   * Is this business currently seeking volunteers?
   */
  seeking_volunteers?: boolean;
  /**
   * Describe the main problems or challenges the business is facing
   */
  problems_facing?: string;
  /**
   * Describe volunteer roles or needs
   */
  volunteer_roles?: string;
  /**
   * Opt-in to receive information about volunteer events and opportunities
   */
  opt_in_for_events?: boolean;
};

export type Submission = {
  readonly id: number;
  team: number;
  readonly team_name: string;
  hackathon: number;
  readonly hackathon_name: string;
  /**
   * Link to the team's code repository.
   */
  repo_url?: string;
  /**
   * Optional demo video link.
   */
  video_url?: string;
  readonly status: SubmissionStatusEnum;
  /**
   * Why the submission failed validation, if it did.
   */
  readonly validation_notes: string;
  /**
   * Immutable roster captured at lock time.
   */
  readonly roster_snapshot: unknown;
  readonly is_locked: boolean;
  readonly locked_at: string | null;
  readonly updated_at: string;
};

/**
 * * `draft` - Draft
 * * `valid` - Valid
 * * `invalid` - Invalid
 * * `locked` - Locked
 */
export type SubmissionStatusEnum = "draft" | "valid" | "invalid" | "locked";

export type Tag = {
  readonly id: number;
  name: string;
  /**
   * Hex color code
   */
  color?: string;
};

export type TeamLeaderboard = {
  rank: number;
  id: number;
  name: string;
  slug: string;
  status: string;
  status_display: string;
  score: number;
};

export type User = {
  readonly id: number;
  email: string;
  name: string;
  phone_number?: string;
  profile_picture?: string | null;
  resume?: string | null;
  /**
   * User's primary address for job matching and location-based features
   */
  address?: number | null;
  readonly address_details: Address;
  address_data?: Address;
  /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean;
  /**
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean;
  /**
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean;
  created?: string;
  readonly modified: string;
  last_login?: string | null;
  role?: RoleEnum;
  /**
   * Specific role within the small business (if applicable)
   *
   * * `owner` - Owner/Founder
   * * `ceo` - CEO/President
   * * `gm` - General Manager
   * * `marketing` - Marketing Manager
   * * `operations` - Operations Manager
   * * `hr` - HR Manager
   * * `finance` - Finance Manager
   * * `sales` - Sales Manager
   * * `it` - IT Manager
   * * `customer_service` - Customer Service Manager
   * * `consultant` - Consultant
   * * `recruiter` - Recruiter
   * * `other` - Other
   */
  business_role?: BusinessRoleEnum | BlankEnum;
  /**
   * Check if this candidate is available as a volunteer
   */
  is_volunteer?: boolean;
  /**
   * Check if this candidate is actively looking for a job
   */
  is_looking_for_job?: boolean;
  company?: number | null;
  readonly applicationids: Array<ApplicationID>;
};

export type UserSkillMatrix = {
  readonly id: number;
  readonly user: number;
  readonly user_name: string;
  readonly user_email: string;
  /**
   * Skill scores and preferences in format: {skill_id: {'score': 0-10, 'preference': 0-10}}
   */
  skills_data?: unknown;
  readonly last_updated: string;
  is_active?: boolean;
};

export type VolunteerOpportunity = {
  readonly id: number;
  company: number;
  readonly company_name: string;
  readonly company_type: string;
  title: string;
  description: string;
  /**
   * Required skill levels and weights
   */
  skills_data?: unknown;
  is_active?: boolean;
  readonly created_at: string;
  readonly updated_at: string;
};

export type WhoAmI = {
  readonly id: number;
  email: string;
  name: string;
  phone_number?: string;
  profile_picture?: string | null;
  /**
   * User's primary address for job matching and location-based features
   */
  address?: number | null;
  readonly address_details: Address;
  /**
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean;
  /**
   * Designates whether the user can log into this admin site.
   */
  is_staff?: boolean;
  /**
   * Designates that this user has all permissions without explicitly assigning them.
   */
  is_superuser?: boolean;
  role?: RoleEnum;
  /**
   * Specific role within the small business (if applicable)
   *
   * * `owner` - Owner/Founder
   * * `ceo` - CEO/President
   * * `gm` - General Manager
   * * `marketing` - Marketing Manager
   * * `operations` - Operations Manager
   * * `hr` - HR Manager
   * * `finance` - Finance Manager
   * * `sales` - Sales Manager
   * * `it` - IT Manager
   * * `customer_service` - Customer Service Manager
   * * `consultant` - Consultant
   * * `recruiter` - Recruiter
   * * `other` - Other
   */
  business_role?: BusinessRoleEnum | BlankEnum;
  /**
   * Check if this candidate is available as a volunteer
   */
  is_volunteer?: boolean;
  /**
   * Check if this candidate is actively looking for a job
   */
  is_looking_for_job?: boolean;
  company?: number | null;
  created?: string;
  readonly modified: string;
  last_login?: string | null;
};

/**
 * * `lt1` - Less than 1 year (Startup)
 * * `1_3` - 1-3 years
 * * `4_7` - 4-7 years
 * * `8_15` - 8-15 years
 * * `15_plus` - More than 15 years
 */
export type YearsOperatingEnum = "lt1" | "1_3" | "4_7" | "8_15" | "15_plus";

export type ApiApplicationListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiApplicationListResponse = PaginatedApplicationList;

export type ApiApplicationCreateData = {
  requestBody: Application;
};

export type ApiApplicationCreateResponse = Application;

export type ApiApplicationRetrieveData = {
  /**
   * A unique integer value identifying this application.
   */
  id: number;
};

export type ApiApplicationRetrieveResponse = Application;

export type ApiApplicationUpdateData = {
  /**
   * A unique integer value identifying this application.
   */
  id: number;
  requestBody: Application;
};

export type ApiApplicationUpdateResponse = Application;

export type ApiApplicationPartialUpdateData = {
  /**
   * A unique integer value identifying this application.
   */
  id: number;
  requestBody?: PatchedApplication;
};

export type ApiApplicationPartialUpdateResponse = Application;

export type ApiApplicationDestroyData = {
  /**
   * A unique integer value identifying this application.
   */
  id: number;
};

export type ApiApplicationDestroyResponse = void;

export type ApiAwardListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiAwardListResponse = PaginatedAwardList;

export type ApiAwardCreateData = {
  requestBody: Award;
};

export type ApiAwardCreateResponse = Award;

export type ApiAwardRetrieveData = {
  /**
   * A unique integer value identifying this award.
   */
  id: number;
};

export type ApiAwardRetrieveResponse = Award;

export type ApiAwardUpdateData = {
  /**
   * A unique integer value identifying this award.
   */
  id: number;
  requestBody: Award;
};

export type ApiAwardUpdateResponse = Award;

export type ApiAwardPartialUpdateData = {
  /**
   * A unique integer value identifying this award.
   */
  id: number;
  requestBody?: PatchedAward;
};

export type ApiAwardPartialUpdateResponse = Award;

export type ApiAwardDestroyData = {
  /**
   * A unique integer value identifying this award.
   */
  id: number;
};

export type ApiAwardDestroyResponse = void;

export type ApiCandidatePreferencesRetrieveResponse = CandidatePreferences;

export type ApiCandidatePreferencesUpdateData = {
  requestBody?: CandidatePreferences;
};

export type ApiCandidatePreferencesUpdateResponse = CandidatePreferences;

export type ApiCertificationListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiCertificationListResponse = PaginatedCertificationList;

export type ApiCertificationCreateData = {
  requestBody: Certification;
};

export type ApiCertificationCreateResponse = Certification;

export type ApiCertificationRetrieveData = {
  /**
   * A unique integer value identifying this certification.
   */
  id: number;
};

export type ApiCertificationRetrieveResponse = Certification;

export type ApiCertificationUpdateData = {
  /**
   * A unique integer value identifying this certification.
   */
  id: number;
  requestBody: Certification;
};

export type ApiCertificationUpdateResponse = Certification;

export type ApiCertificationPartialUpdateData = {
  /**
   * A unique integer value identifying this certification.
   */
  id: number;
  requestBody?: PatchedCertification;
};

export type ApiCertificationPartialUpdateResponse = Certification;

export type ApiCertificationDestroyData = {
  /**
   * A unique integer value identifying this certification.
   */
  id: number;
};

export type ApiCertificationDestroyResponse = void;

export type ApiChangelogListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiChangelogListResponse = PaginatedChangeLogEntryList;

export type ApiChangelogRetrieveData = {
  /**
   * A unique integer value identifying this change log entry.
   */
  id: number;
};

export type ApiChangelogRetrieveResponse = ChangeLogEntry;

export type ApiCompanyListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiCompanyListResponse = PaginatedCompanyList;

export type ApiCompanyCreateData = {
  requestBody: Company;
};

export type ApiCompanyCreateResponse = Company;

export type ApiCompanyRetrieveData = {
  /**
   * A unique integer value identifying this company.
   */
  id: number;
};

export type ApiCompanyRetrieveResponse = Company;

export type ApiCompanyUpdateData = {
  /**
   * A unique integer value identifying this company.
   */
  id: number;
  requestBody: Company;
};

export type ApiCompanyUpdateResponse = Company;

export type ApiCompanyPartialUpdateData = {
  /**
   * A unique integer value identifying this company.
   */
  id: number;
  requestBody?: PatchedCompany;
};

export type ApiCompanyPartialUpdateResponse = Company;

export type ApiCompanyDestroyData = {
  /**
   * A unique integer value identifying this company.
   */
  id: number;
};

export type ApiCompanyDestroyResponse = void;

export type ApiEducationListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiEducationListResponse = PaginatedEducationList;

export type ApiEducationCreateData = {
  requestBody: Education;
};

export type ApiEducationCreateResponse = Education;

export type ApiEducationRetrieveData = {
  /**
   * A unique integer value identifying this education.
   */
  id: number;
};

export type ApiEducationRetrieveResponse = Education;

export type ApiEducationUpdateData = {
  /**
   * A unique integer value identifying this education.
   */
  id: number;
  requestBody: Education;
};

export type ApiEducationUpdateResponse = Education;

export type ApiEducationPartialUpdateData = {
  /**
   * A unique integer value identifying this education.
   */
  id: number;
  requestBody?: PatchedEducation;
};

export type ApiEducationPartialUpdateResponse = Education;

export type ApiEducationDestroyData = {
  /**
   * A unique integer value identifying this education.
   */
  id: number;
};

export type ApiEducationDestroyResponse = void;

export type ApiEventsListData = {
  city?: string;
  cost?: number;
  costGte?: number;
  costLte?: number;
  country?: string;
  description?: string;
  endDateFrom?: string;
  endDateTo?: string;
  /**
   * * `workshop` - Workshop
   * * `seminar` - Seminar
   * * `conference` - Conference
   * * `networking` - Networking
   * * `training` - Training
   * * `webinar` - Webinar
   * * `other` - Other
   */
  eventType?:
    | "conference"
    | "networking"
    | "other"
    | "seminar"
    | "training"
    | "webinar"
    | "workshop";
  /**
   * Multiple values may be separated by commas.
   */
  eventTypeIn?: Array<string>;
  isActive?: boolean;
  isFree?: boolean;
  isOnline?: boolean;
  /**
   * Number of results to return per page.
   */
  limit?: number;
  location?: string;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
  organizerName?: string;
  registrationOpen?: boolean;
  registrationRequired?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
  tags?: Array<string>;
  title?: string;
  upcoming?: boolean;
};

export type ApiEventsListResponse = PaginatedEventListList;

export type ApiEventsCreateData = {
  requestBody: Event;
};

export type ApiEventsCreateResponse = Event;

export type ApiEventsRetrieveData = {
  /**
   * A unique integer value identifying this event.
   */
  id: number;
};

export type ApiEventsRetrieveResponse = Event;

export type ApiEventsUpdateData = {
  /**
   * A unique integer value identifying this event.
   */
  id: number;
  requestBody: Event;
};

export type ApiEventsUpdateResponse = Event;

export type ApiEventsPartialUpdateData = {
  /**
   * A unique integer value identifying this event.
   */
  id: number;
  requestBody?: PatchedEvent;
};

export type ApiEventsPartialUpdateResponse = Event;

export type ApiEventsDestroyData = {
  /**
   * A unique integer value identifying this event.
   */
  id: number;
};

export type ApiEventsDestroyResponse = void;

export type ApiEventsIncrementParticipantsCreateData = {
  /**
   * A unique integer value identifying this event.
   */
  id: number;
  requestBody: Event;
};

export type ApiEventsIncrementParticipantsCreateResponse = Event;

export type ApiEventsTodayRetrieveResponse = Event;

export type ApiEventsUpcomingRetrieveResponse = Event;

export type ApiExperienceListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiExperienceListResponse = PaginatedExperienceList;

export type ApiExperienceCreateData = {
  requestBody: Experience;
};

export type ApiExperienceCreateResponse = Experience;

export type ApiExperienceRetrieveData = {
  /**
   * A unique integer value identifying this experience.
   */
  id: number;
};

export type ApiExperienceRetrieveResponse = Experience;

export type ApiExperienceUpdateData = {
  /**
   * A unique integer value identifying this experience.
   */
  id: number;
  requestBody: Experience;
};

export type ApiExperienceUpdateResponse = Experience;

export type ApiExperiencePartialUpdateData = {
  /**
   * A unique integer value identifying this experience.
   */
  id: number;
  requestBody?: PatchedExperience;
};

export type ApiExperiencePartialUpdateResponse = Experience;

export type ApiExperienceDestroyData = {
  /**
   * A unique integer value identifying this experience.
   */
  id: number;
};

export type ApiExperienceDestroyResponse = void;

export type ApiGoogleloginCreateData = {
  requestBody: GoogleAuth;
};

export type ApiGoogleloginCreateResponse = GoogleAuth;

export type ApiJobSkillMatricesListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiJobSkillMatricesListResponse = PaginatedJobSkillMatrixList;

export type ApiJobSkillMatricesCreateData = {
  requestBody: JobSkillMatrix;
};

export type ApiJobSkillMatricesCreateResponse = JobSkillMatrix;

export type ApiJobSkillMatricesRetrieveData = {
  /**
   * A unique integer value identifying this job skill matrix.
   */
  id: number;
};

export type ApiJobSkillMatricesRetrieveResponse = JobSkillMatrix;

export type ApiJobSkillMatricesUpdateData = {
  /**
   * A unique integer value identifying this job skill matrix.
   */
  id: number;
  requestBody: JobSkillMatrix;
};

export type ApiJobSkillMatricesUpdateResponse = JobSkillMatrix;

export type ApiJobSkillMatricesPartialUpdateData = {
  /**
   * A unique integer value identifying this job skill matrix.
   */
  id: number;
  requestBody?: PatchedJobSkillMatrix;
};

export type ApiJobSkillMatricesPartialUpdateResponse = JobSkillMatrix;

export type ApiJobSkillMatricesDestroyData = {
  /**
   * A unique integer value identifying this job skill matrix.
   */
  id: number;
};

export type ApiJobSkillMatricesDestroyResponse = void;

export type ApiJobSkillMatricesUpdateSkillCreateData = {
  /**
   * A unique integer value identifying this job skill matrix.
   */
  id: number;
  requestBody: JobSkillMatrix;
};

export type ApiJobSkillMatricesUpdateSkillCreateResponse = JobSkillMatrix;

export type ApiJobprofileListData = {
  city?: string;
  country?: string;
  description?: string;
  isRemote?: boolean;
  /**
   * * `full-time` - Full Time
   * * `intern` - Intern
   * * `volunteering` - Volunteering
   */
  jobType?: "full-time" | "intern" | "volunteering";
  /**
   * Multiple values may be separated by commas.
   */
  jobTypeIn?: Array<string>;
  keywords?: string;
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
  salaryMax?: number;
  salaryMin?: number;
  state?: string;
  title?: string;
  visaSponsor?: boolean;
};

export type ApiJobprofileListResponse = PaginatedJobProfileList;

export type ApiJobprofileCreateData = {
  requestBody: JobProfile;
};

export type ApiJobprofileCreateResponse = JobProfile;

export type ApiJobprofileRetrieveData = {
  /**
   * A unique integer value identifying this job profile.
   */
  id: number;
};

export type ApiJobprofileRetrieveResponse = JobProfile;

export type ApiJobprofileUpdateData = {
  /**
   * A unique integer value identifying this job profile.
   */
  id: number;
  requestBody: JobProfile;
};

export type ApiJobprofileUpdateResponse = JobProfile;

export type ApiJobprofilePartialUpdateData = {
  /**
   * A unique integer value identifying this job profile.
   */
  id: number;
  requestBody?: PatchedJobProfile;
};

export type ApiJobprofilePartialUpdateResponse = JobProfile;

export type ApiJobprofileDestroyData = {
  /**
   * A unique integer value identifying this job profile.
   */
  id: number;
};

export type ApiJobprofileDestroyResponse = void;

export type ApiLeagueCommissionerRetrieveResponse = unknown;

export type ApiLeagueCommissionerLedgerCreateResponse = unknown;

export type ApiLeagueCommissionerRosterCreateResponse = unknown;

export type ApiLeagueCommissionerTimelineCreateResponse = unknown;

export type ApiLeagueFreeAgentsRetrieveResponse = unknown;

export type ApiLeagueFreeAgentsMeRetrieveResponse = FreeAgent;

export type ApiLeagueFreeAgentsMeUpdateResponse = FreeAgent;

export type ApiLeagueJudgingRetrieveResponse = unknown;

export type ApiLeagueJudgingCreateResponse = unknown;

export type ApiLeagueJudgingFinalizeCreateResponse = unknown;

export type ApiLeagueLeaderboardRetrieveResponse = unknown;

export type ApiLeagueLeaderboardChaptersListData = {
  /**
   * Filter to a single chapter tier.
   */
  tier?: string;
};

export type ApiLeagueLeaderboardChaptersListResponse =
  Array<ChapterLeaderboard>;

export type ApiLeagueLeaderboardDevelopersListData = {
  /**
   * Filter to a single chapter tier.
   */
  tier?: string;
};

export type ApiLeagueLeaderboardDevelopersListResponse =
  Array<DeveloperLeaderboard>;

export type ApiLeagueLeaderboardTeamsListData = {
  /**
   * Filter to a single team status.
   */
  status?: string;
};

export type ApiLeagueLeaderboardTeamsListResponse = Array<TeamLeaderboard>;

export type ApiLeagueModeRetrieveResponse = unknown;

export type ApiLeagueModeSwitchCreateResponse = unknown;

export type ApiLeagueMyChallengesRetrieveResponse = unknown;

export type ApiLeagueMyScoreRetrieveResponse = unknown;

export type ApiLeagueNotificationsRetrieveResponse = unknown;

export type ApiLeagueNotificationsReadAllCreateResponse = unknown;

export type ApiLeagueOrganizerRetrieveResponse = unknown;

export type ApiLeaguePhasesRetrieveData = {
  id: string;
};

export type ApiLeaguePhasesRetrieveResponse = unknown;

export type ApiLeaguePhasesBracketCreateData = {
  id: string;
};

export type ApiLeaguePhasesBracketCreateResponse = unknown;

export type ApiLeaguePhasesFreezeCreateData = {
  id: string;
};

export type ApiLeaguePhasesFreezeCreateResponse = unknown;

export type ApiLeagueRecruitRetrieveResponse = unknown;

export type ApiLeagueRecruitCoMembershipCreateResponse = unknown;

export type ApiLeagueRosterInvitesRetrieveResponse = unknown;

export type ApiLeagueRosterInvitesCreateResponse = unknown;

export type ApiLeagueRosterInvitesApproveCreateData = {
  id: string;
};

export type ApiLeagueRosterInvitesApproveCreateResponse = unknown;

export type ApiLeagueRosterInvitesConfirmCreateData = {
  id: string;
};

export type ApiLeagueRosterInvitesConfirmCreateResponse = unknown;

export type ApiLeagueRosterInvitesDeclineCreateData = {
  id: string;
};

export type ApiLeagueRosterInvitesDeclineCreateResponse = unknown;

export type ApiLeagueRosterInvitesLockCreateData = {
  id: string;
};

export type ApiLeagueRosterInvitesLockCreateResponse = unknown;

export type ApiLeagueSeasonsRetrieveResponse = unknown;

export type ApiLeagueSeasonsRetrieve2Data = {
  id: string;
};

export type ApiLeagueSeasonsRetrieve2Response = unknown;

export type ApiLeagueSoloTeamCreateResponse = unknown;

export type ApiLeagueSubmissionsListResponse = Array<Submission>;

export type ApiLeagueSubmissionsCreateData = {
  requestBody: Submission;
};

export type ApiLeagueSubmissionsCreateResponse = Submission;

export type ApiLeagueSubmissionsRetrieveData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
};

export type ApiLeagueSubmissionsRetrieveResponse = Submission;

export type ApiLeagueSubmissionsUpdateData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
  requestBody: Submission;
};

export type ApiLeagueSubmissionsUpdateResponse = Submission;

export type ApiLeagueSubmissionsPartialUpdateData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
  requestBody?: PatchedSubmission;
};

export type ApiLeagueSubmissionsPartialUpdateResponse = Submission;

export type ApiLeagueSubmissionsDestroyData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
};

export type ApiLeagueSubmissionsDestroyResponse = void;

export type ApiLeagueSubmissionsLockCreateData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
  requestBody: Submission;
};

export type ApiLeagueSubmissionsLockCreateResponse = Submission;

export type ApiLeagueSubmissionsValidateCreateData = {
  /**
   * A unique integer value identifying this submission.
   */
  id: number;
  requestBody: Submission;
};

export type ApiLeagueSubmissionsValidateCreateResponse = Submission;

export type ApiLeagueSubmissionsOptionsRetrieveResponse = Submission;

export type ApiLeagueTeamMatchesRetrieveData = {
  id: string;
};

export type ApiLeagueTeamMatchesRetrieveResponse = unknown;

export type ApiLoginCreateData = {
  requestBody: Login;
};

export type ApiLoginCreateResponse = Login;

export type ApiLogoutCreateResponse = unknown;

export type ApiMatchResultsListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiMatchResultsListResponse = PaginatedMatchResultList;

export type ApiMatchResultsRetrieveData = {
  /**
   * A unique integer value identifying this match result.
   */
  id: number;
};

export type ApiMatchResultsRetrieveResponse = MatchResult;

export type ApiMatchResultsUpdateData = {
  /**
   * A unique integer value identifying this match result.
   */
  id: number;
  requestBody?: MatchResult;
};

export type ApiMatchResultsUpdateResponse = MatchResult;

export type ApiMatchResultsPartialUpdateData = {
  /**
   * A unique integer value identifying this match result.
   */
  id: number;
  requestBody?: PatchedMatchResult;
};

export type ApiMatchResultsPartialUpdateResponse = MatchResult;

export type ApiMatchResultsSaveJobCreateData = {
  requestBody?: MatchResult;
};

export type ApiMatchResultsSaveJobCreateResponse = MatchResult;

export type ApiMatchingSessionsListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiMatchingSessionsListResponse = PaginatedMatchingSessionList;

export type ApiMatchingSessionsRetrieveData = {
  /**
   * A unique integer value identifying this matching session.
   */
  id: number;
};

export type ApiMatchingSessionsRetrieveResponse = MatchingSession;

export type ApiMatchingSkillsListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiMatchingSkillsListResponse = PaginatedSkillList;

export type ApiMatchingSkillsRetrieveData = {
  /**
   * A unique integer value identifying this skill.
   */
  id: number;
};

export type ApiMatchingSkillsRetrieveResponse = Skill;

export type ApiMatchingSkillsAutocompleteRetrieveResponse = Skill;

export type ApiMatchingAnalyticsRetrieveResponse = MatchingSession;

export type ApiMatchingExtractSkillsCreateData = {
  requestBody: Skill;
};

export type ApiMatchingExtractSkillsCreateResponse = Skill;

export type ApiMatchingMatchRetrieveResponse = MatchResult;

export type ApiMatchingMatchCreateData = {
  requestBody?: MatchResult;
};

export type ApiMatchingMatchCreateResponse = MatchResult;

export type ApiMatchingTaskStatusRetrieveResponse = unknown;

export type ApiNonprofitProfileListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiNonprofitProfileListResponse = PaginatedNonProfitProfileList;

export type ApiNonprofitProfileCreateData = {
  requestBody: NonProfitProfile;
};

export type ApiNonprofitProfileCreateResponse = NonProfitProfile;

export type ApiNonprofitProfileRetrieveData = {
  /**
   * A unique integer value identifying this non profit profile.
   */
  id: number;
};

export type ApiNonprofitProfileRetrieveResponse = NonProfitProfile;

export type ApiNonprofitProfileUpdateData = {
  /**
   * A unique integer value identifying this non profit profile.
   */
  id: number;
  requestBody: NonProfitProfile;
};

export type ApiNonprofitProfileUpdateResponse = NonProfitProfile;

export type ApiNonprofitProfilePartialUpdateData = {
  /**
   * A unique integer value identifying this non profit profile.
   */
  id: number;
  requestBody?: PatchedNonProfitProfile;
};

export type ApiNonprofitProfilePartialUpdateResponse = NonProfitProfile;

export type ApiNonprofitProfileDestroyData = {
  /**
   * A unique integer value identifying this non profit profile.
   */
  id: number;
};

export type ApiNonprofitProfileDestroyResponse = void;

export type ApiProfileListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiProfileListResponse = PaginatedProfileList;

export type ApiProfileCreateData = {
  requestBody: Profile;
};

export type ApiProfileCreateResponse = Profile;

export type ApiProfileRetrieveData = {
  /**
   * A unique integer value identifying this profile.
   */
  id: number;
};

export type ApiProfileRetrieveResponse = Profile;

export type ApiProfileUpdateData = {
  /**
   * A unique integer value identifying this profile.
   */
  id: number;
  requestBody: Profile;
};

export type ApiProfileUpdateResponse = Profile;

export type ApiProfilePartialUpdateData = {
  /**
   * A unique integer value identifying this profile.
   */
  id: number;
  requestBody?: PatchedProfile;
};

export type ApiProfilePartialUpdateResponse = Profile;

export type ApiProfileDestroyData = {
  /**
   * A unique integer value identifying this profile.
   */
  id: number;
};

export type ApiProfileDestroyResponse = void;

export type ApiProjectListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiProjectListResponse = PaginatedProjectList;

export type ApiProjectCreateData = {
  requestBody: Project;
};

export type ApiProjectCreateResponse = Project;

export type ApiProjectRetrieveData = {
  /**
   * A unique integer value identifying this project.
   */
  id: number;
};

export type ApiProjectRetrieveResponse = Project;

export type ApiProjectUpdateData = {
  /**
   * A unique integer value identifying this project.
   */
  id: number;
  requestBody: Project;
};

export type ApiProjectUpdateResponse = Project;

export type ApiProjectPartialUpdateData = {
  /**
   * A unique integer value identifying this project.
   */
  id: number;
  requestBody?: PatchedProject;
};

export type ApiProjectPartialUpdateResponse = Project;

export type ApiProjectDestroyData = {
  /**
   * A unique integer value identifying this project.
   */
  id: number;
};

export type ApiProjectDestroyResponse = void;

export type ApiPublicationListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiPublicationListResponse = PaginatedPublicationList;

export type ApiPublicationCreateData = {
  requestBody: Publication;
};

export type ApiPublicationCreateResponse = Publication;

export type ApiPublicationRetrieveData = {
  /**
   * A unique integer value identifying this publication.
   */
  id: number;
};

export type ApiPublicationRetrieveResponse = Publication;

export type ApiPublicationUpdateData = {
  /**
   * A unique integer value identifying this publication.
   */
  id: number;
  requestBody: Publication;
};

export type ApiPublicationUpdateResponse = Publication;

export type ApiPublicationPartialUpdateData = {
  /**
   * A unique integer value identifying this publication.
   */
  id: number;
  requestBody?: PatchedPublication;
};

export type ApiPublicationPartialUpdateResponse = Publication;

export type ApiPublicationDestroyData = {
  /**
   * A unique integer value identifying this publication.
   */
  id: number;
};

export type ApiPublicationDestroyResponse = void;

export type ApiResourcesListData = {
  author?: string;
  city?: string;
  cost?: number;
  costGte?: number;
  costLte?: number;
  country?: string;
  description?: string;
  isActive?: boolean;
  isFree?: boolean;
  language?: string;
  /**
   * Multiple values may be separated by commas.
   */
  languageIn?: Array<string>;
  /**
   * Number of results to return per page.
   */
  limit?: number;
  location?: string;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
  publishedDateFrom?: string;
  publishedDateTo?: string;
  publisher?: string;
  /**
   * * `article` - Article
   * * `video` - Video
   * * `podcast` - Podcast
   * * `ebook` - E-Book
   * * `template` - Template
   * * `tool` - Tool
   * * `course` - Course
   * * `guide` - Guide
   * * `other` - Other
   */
  resourceType?:
    | "article"
    | "course"
    | "ebook"
    | "guide"
    | "other"
    | "podcast"
    | "template"
    | "tool"
    | "video";
  /**
   * Multiple values may be separated by commas.
   */
  resourceTypeIn?: Array<string>;
  tags?: Array<string>;
  title?: string;
};

export type ApiResourcesListResponse = PaginatedResourceListList;

export type ApiResourcesCreateData = {
  requestBody: Resource;
};

export type ApiResourcesCreateResponse = Resource;

export type ApiResourcesRetrieveData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
};

export type ApiResourcesRetrieveResponse = Resource;

export type ApiResourcesUpdateData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
  requestBody: Resource;
};

export type ApiResourcesUpdateResponse = Resource;

export type ApiResourcesPartialUpdateData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
  requestBody?: PatchedResource;
};

export type ApiResourcesPartialUpdateResponse = Resource;

export type ApiResourcesDestroyData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
};

export type ApiResourcesDestroyResponse = void;

export type ApiResourcesIncrementDownloadsCreateData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
  requestBody: Resource;
};

export type ApiResourcesIncrementDownloadsCreateResponse = Resource;

export type ApiResourcesIncrementViewsCreateData = {
  /**
   * A unique integer value identifying this resource.
   */
  id: number;
  requestBody: Resource;
};

export type ApiResourcesIncrementViewsCreateResponse = Resource;

export type ApiResourcesFreeRetrieveResponse = Resource;

export type ApiResourcesPopularRetrieveResponse = Resource;

export type ApiRestRestCheckRetrieveResponse = Message;

export type ApiSessionRetrieveResponse = Session;

export type ApiSignupCreateData = {
  requestBody: Signup;
};

export type ApiSignupCreateResponse = Signup;

export type ApiSkillListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiSkillListResponse = PaginatedApplicationSkillList;

export type ApiSkillCreateData = {
  requestBody: ApplicationSkill;
};

export type ApiSkillCreateResponse = ApplicationSkill;

export type ApiSkillCategoriesListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiSkillCategoriesListResponse = PaginatedSkillCategoryList;

export type ApiSkillCategoriesRetrieveData = {
  /**
   * A unique integer value identifying this skill category.
   */
  id: number;
};

export type ApiSkillCategoriesRetrieveResponse = SkillCategory;

export type ApiSkillRetrieveData = {
  /**
   * A unique integer value identifying this skill.
   */
  id: number;
};

export type ApiSkillRetrieveResponse = ApplicationSkill;

export type ApiSkillUpdateData = {
  /**
   * A unique integer value identifying this skill.
   */
  id: number;
  requestBody: ApplicationSkill;
};

export type ApiSkillUpdateResponse = ApplicationSkill;

export type ApiSkillPartialUpdateData = {
  /**
   * A unique integer value identifying this skill.
   */
  id: number;
  requestBody?: PatchedApplicationSkill;
};

export type ApiSkillPartialUpdateResponse = ApplicationSkill;

export type ApiSkillDestroyData = {
  /**
   * A unique integer value identifying this skill.
   */
  id: number;
};

export type ApiSkillDestroyResponse = void;

export type ApiSmallbizProfileListData = {
  companyName?: string;
  hasProblem?: boolean;
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
  problemsFacing?: string;
  seekingVolunteers?: boolean;
  /**
   * * `lt1` - Less than 1 year (Startup)
   * * `1_3` - 1-3 years
   * * `4_7` - 4-7 years
   * * `8_15` - 8-15 years
   * * `15_plus` - More than 15 years
   */
  yearsOperating?: "15_plus" | "1_3" | "4_7" | "8_15" | "lt1";
};

export type ApiSmallbizProfileListResponse = PaginatedSmallBusinessProfileList;

export type ApiSmallbizProfileCreateData = {
  requestBody: SmallBusinessProfile;
};

export type ApiSmallbizProfileCreateResponse = SmallBusinessProfile;

export type ApiSmallbizProfileRetrieveData = {
  /**
   * A unique integer value identifying this small business profile.
   */
  id: number;
};

export type ApiSmallbizProfileRetrieveResponse = SmallBusinessProfile;

export type ApiSmallbizProfileUpdateData = {
  /**
   * A unique integer value identifying this small business profile.
   */
  id: number;
  requestBody: SmallBusinessProfile;
};

export type ApiSmallbizProfileUpdateResponse = SmallBusinessProfile;

export type ApiSmallbizProfilePartialUpdateData = {
  /**
   * A unique integer value identifying this small business profile.
   */
  id: number;
  requestBody?: PatchedSmallBusinessProfile;
};

export type ApiSmallbizProfilePartialUpdateResponse = SmallBusinessProfile;

export type ApiSmallbizProfileDestroyData = {
  /**
   * A unique integer value identifying this small business profile.
   */
  id: number;
};

export type ApiSmallbizProfileDestroyResponse = void;

export type ApiTagsListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiTagsListResponse = PaginatedTagList;

export type ApiTagsRetrieveData = {
  /**
   * A unique integer value identifying this tag.
   */
  id: number;
};

export type ApiTagsRetrieveResponse = Tag;

export type ApiTagsPopularRetrieveResponse = Tag;

export type ApiUserSkillMatricesListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiUserSkillMatricesListResponse = PaginatedUserSkillMatrixList;

export type ApiUserSkillMatricesCreateData = {
  requestBody?: UserSkillMatrix;
};

export type ApiUserSkillMatricesCreateResponse = UserSkillMatrix;

export type ApiUserSkillMatricesRetrieveData = {
  /**
   * A unique integer value identifying this user skill matrix.
   */
  id: number;
};

export type ApiUserSkillMatricesRetrieveResponse = UserSkillMatrix;

export type ApiUserSkillMatricesUpdateData = {
  /**
   * A unique integer value identifying this user skill matrix.
   */
  id: number;
  requestBody?: UserSkillMatrix;
};

export type ApiUserSkillMatricesUpdateResponse = UserSkillMatrix;

export type ApiUserSkillMatricesPartialUpdateData = {
  /**
   * A unique integer value identifying this user skill matrix.
   */
  id: number;
  requestBody?: PatchedUserSkillMatrix;
};

export type ApiUserSkillMatricesPartialUpdateResponse = UserSkillMatrix;

export type ApiUserSkillMatricesDestroyData = {
  /**
   * A unique integer value identifying this user skill matrix.
   */
  id: number;
};

export type ApiUserSkillMatricesDestroyResponse = void;

export type ApiUserSkillMatricesUpdateSkillCreateData = {
  /**
   * A unique integer value identifying this user skill matrix.
   */
  id: number;
  requestBody?: UserSkillMatrix;
};

export type ApiUserSkillMatricesUpdateSkillCreateResponse = UserSkillMatrix;

export type ApiUsersListData = {
  isActive?: boolean;
  isLookingForJob?: boolean;
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
  /**
   * * `admin` - Admin
   * * `staff` - Staff
   * * `candidate` - Candidate
   * * `recruiter` - Recruiter
   * * `smallbiz_owner` - Small Business Owner
   * * `nonprofit` - Nonprofit Organization
   */
  role?:
    | "admin"
    | "candidate"
    | "nonprofit"
    | "recruiter"
    | "smallbiz_owner"
    | "staff";
  /**
   * A search term.
   */
  search?: string;
};

export type ApiUsersListResponse = PaginatedUserList;

export type ApiUsersCreateData = {
  requestBody: User;
};

export type ApiUsersCreateResponse = User;

export type ApiUsersRetrieveData = {
  /**
   * A unique integer value identifying this user.
   */
  id: number;
};

export type ApiUsersRetrieveResponse = User;

export type ApiUsersUpdateData = {
  /**
   * A unique integer value identifying this user.
   */
  id: number;
  requestBody: User;
};

export type ApiUsersUpdateResponse = User;

export type ApiUsersPartialUpdateData = {
  /**
   * A unique integer value identifying this user.
   */
  id: number;
  requestBody?: PatchedUser;
};

export type ApiUsersPartialUpdateResponse = User;

export type ApiUsersDestroyData = {
  /**
   * A unique integer value identifying this user.
   */
  id: number;
};

export type ApiUsersDestroyResponse = void;

export type ApiUsersDashboardJobSearchStatsRetrieveData = {
  /**
   * Number of days to look back for stats (default: 30)
   */
  days?: number;
};

export type ApiUsersDashboardJobSearchStatsRetrieveResponse = {
  applications?: number;
  job_matches?: number;
  screens?: number;
  offers?: number;
};

export type ApiUsersDashboardProfileCompletionRetrieveResponse = {
  completion?: number;
  checklist?: Array<{
    label?: string;
    done?: boolean;
  }>;
};

export type ApiUsersDashboardRecentActivityRetrieveResponse = {
  activities?: Array<{
    id?: number;
    type?: string;
    company?: string;
    position?: string;
    date?: string;
    status?: string;
  }>;
};

export type ApiVolunteerOpportunitiesListData = {
  /**
   * Number of results to return per page.
   */
  limit?: number;
  /**
   * The initial index from which to return the results.
   */
  offset?: number;
};

export type ApiVolunteerOpportunitiesListResponse =
  PaginatedVolunteerOpportunityList;

export type ApiVolunteerOpportunitiesCreateData = {
  requestBody: VolunteerOpportunity;
};

export type ApiVolunteerOpportunitiesCreateResponse = VolunteerOpportunity;

export type ApiVolunteerOpportunitiesRetrieveData = {
  /**
   * A unique integer value identifying this volunteer opportunity.
   */
  id: number;
};

export type ApiVolunteerOpportunitiesRetrieveResponse = VolunteerOpportunity;

export type ApiVolunteerOpportunitiesUpdateData = {
  /**
   * A unique integer value identifying this volunteer opportunity.
   */
  id: number;
  requestBody: VolunteerOpportunity;
};

export type ApiVolunteerOpportunitiesUpdateResponse = VolunteerOpportunity;

export type ApiVolunteerOpportunitiesPartialUpdateData = {
  /**
   * A unique integer value identifying this volunteer opportunity.
   */
  id: number;
  requestBody?: PatchedVolunteerOpportunity;
};

export type ApiVolunteerOpportunitiesPartialUpdateResponse =
  VolunteerOpportunity;

export type ApiVolunteerOpportunitiesDestroyData = {
  /**
   * A unique integer value identifying this volunteer opportunity.
   */
  id: number;
};

export type ApiVolunteerOpportunitiesDestroyResponse = void;

export type ApiVolunteerOpportunitiesMyOpportunitiesRetrieveResponse =
  VolunteerOpportunity;

export type ApiWhoamiRetrieveResponse = WhoAmI;

export type ApiCandidateProfileRetrieveData = {
  /**
   * ID of the candidate to view
   */
  candidateId: number;
};

export type ApiCandidateProfileRetrieveResponse = CandidatePublicProfile;

export type ApiSearchCandidatesRetrieveData = {
  /**
   * Maximum number of results
   */
  limit?: number;
  /**
   * Search query
   */
  q: string;
};

export type ApiSearchCandidatesRetrieveResponse = SearchResponse;

export type ApiSearchCandidatesCreateData = {
  /**
   * Maximum number of results (default: 20, max: 100)
   */
  limit?: number;
  /**
   * Natural language search query
   */
  query: string;
  requestBody: SearchRequest;
};

export type ApiSearchCandidatesCreateResponse = SearchResponse;

export type ApiSearchSuggestionsRetrieveData = {
  /**
   * Partial search query
   */
  q: string;
};

export type ApiSearchSuggestionsRetrieveResponse = unknown;

export type ApiSendEmailCreateData = {
  requestBody: SendEmail;
};

export type ApiSendEmailCreateResponse = unknown;

export type CommonRestCheckRetrieveResponse = Message;

export type CommonResumeAutoPrefillRetrieveData = {
  taskId: string;
};

export type CommonResumeAutoPrefillRetrieveResponse = Message;

export type CommonResumeEnhancedCreateData = {
  requestBody: Message;
};

export type CommonResumeEnhancedCreateResponse = Message;

export type CommonResumeResultRetrieveData = {
  taskId: string;
};

export type CommonResumeResultRetrieveResponse = Message;

export type CommonResumeUploadRetrieveResponse = unknown;

export type CommonResumeUploadCreateData = {
  formData?: {
    /**
     * Resume file (PDF, DOC, DOCX)
     */
    file?: Blob | File;
    /**
     * Resume file (alternative field name)
     */
    resume?: Blob | File;
    /**
     * Use enhanced parser (default: false)
     */
    enhanced?: boolean;
  };
};

export type CommonResumeUploadCreateResponse = unknown;

export type CommonResumeUploadUpdateData = {
  formData?: {
    /**
     * Resume file (PDF, DOC, DOCX)
     */
    file?: Blob | File;
    /**
     * Resume file (alternative field name)
     */
    resume?: Blob | File;
    /**
     * Use enhanced parser (default: false)
     */
    enhanced?: boolean;
  };
};

export type CommonResumeUploadUpdateResponse = unknown;

export type CommonResumeUploadDestroyResponse = unknown;

export type $OpenApiTs = {
  "/api/application/": {
    get: {
      req: ApiApplicationListData;
      res: {
        200: PaginatedApplicationList;
      };
    };
    post: {
      req: ApiApplicationCreateData;
      res: {
        201: Application;
      };
    };
  };
  "/api/application/{id}/": {
    get: {
      req: ApiApplicationRetrieveData;
      res: {
        200: Application;
      };
    };
    put: {
      req: ApiApplicationUpdateData;
      res: {
        200: Application;
      };
    };
    patch: {
      req: ApiApplicationPartialUpdateData;
      res: {
        200: Application;
      };
    };
    delete: {
      req: ApiApplicationDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/award/": {
    get: {
      req: ApiAwardListData;
      res: {
        200: PaginatedAwardList;
      };
    };
    post: {
      req: ApiAwardCreateData;
      res: {
        201: Award;
      };
    };
  };
  "/api/award/{id}/": {
    get: {
      req: ApiAwardRetrieveData;
      res: {
        200: Award;
      };
    };
    put: {
      req: ApiAwardUpdateData;
      res: {
        200: Award;
      };
    };
    patch: {
      req: ApiAwardPartialUpdateData;
      res: {
        200: Award;
      };
    };
    delete: {
      req: ApiAwardDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/candidate-preferences/": {
    get: {
      res: {
        200: CandidatePreferences;
      };
    };
    put: {
      req: ApiCandidatePreferencesUpdateData;
      res: {
        200: CandidatePreferences;
      };
    };
  };
  "/api/certification/": {
    get: {
      req: ApiCertificationListData;
      res: {
        200: PaginatedCertificationList;
      };
    };
    post: {
      req: ApiCertificationCreateData;
      res: {
        201: Certification;
      };
    };
  };
  "/api/certification/{id}/": {
    get: {
      req: ApiCertificationRetrieveData;
      res: {
        200: Certification;
      };
    };
    put: {
      req: ApiCertificationUpdateData;
      res: {
        200: Certification;
      };
    };
    patch: {
      req: ApiCertificationPartialUpdateData;
      res: {
        200: Certification;
      };
    };
    delete: {
      req: ApiCertificationDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/changelog/": {
    get: {
      req: ApiChangelogListData;
      res: {
        200: PaginatedChangeLogEntryList;
      };
    };
  };
  "/api/changelog/{id}/": {
    get: {
      req: ApiChangelogRetrieveData;
      res: {
        200: ChangeLogEntry;
      };
    };
  };
  "/api/company/": {
    get: {
      req: ApiCompanyListData;
      res: {
        200: PaginatedCompanyList;
      };
    };
    post: {
      req: ApiCompanyCreateData;
      res: {
        201: Company;
      };
    };
  };
  "/api/company/{id}/": {
    get: {
      req: ApiCompanyRetrieveData;
      res: {
        200: Company;
      };
    };
    put: {
      req: ApiCompanyUpdateData;
      res: {
        200: Company;
      };
    };
    patch: {
      req: ApiCompanyPartialUpdateData;
      res: {
        200: Company;
      };
    };
    delete: {
      req: ApiCompanyDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/education/": {
    get: {
      req: ApiEducationListData;
      res: {
        200: PaginatedEducationList;
      };
    };
    post: {
      req: ApiEducationCreateData;
      res: {
        201: Education;
      };
    };
  };
  "/api/education/{id}/": {
    get: {
      req: ApiEducationRetrieveData;
      res: {
        200: Education;
      };
    };
    put: {
      req: ApiEducationUpdateData;
      res: {
        200: Education;
      };
    };
    patch: {
      req: ApiEducationPartialUpdateData;
      res: {
        200: Education;
      };
    };
    delete: {
      req: ApiEducationDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/events/": {
    get: {
      req: ApiEventsListData;
      res: {
        200: PaginatedEventListList;
      };
    };
    post: {
      req: ApiEventsCreateData;
      res: {
        201: Event;
      };
    };
  };
  "/api/events/{id}/": {
    get: {
      req: ApiEventsRetrieveData;
      res: {
        200: Event;
      };
    };
    put: {
      req: ApiEventsUpdateData;
      res: {
        200: Event;
      };
    };
    patch: {
      req: ApiEventsPartialUpdateData;
      res: {
        200: Event;
      };
    };
    delete: {
      req: ApiEventsDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/events/{id}/increment_participants/": {
    post: {
      req: ApiEventsIncrementParticipantsCreateData;
      res: {
        200: Event;
      };
    };
  };
  "/api/events/today/": {
    get: {
      res: {
        200: Event;
      };
    };
  };
  "/api/events/upcoming/": {
    get: {
      res: {
        200: Event;
      };
    };
  };
  "/api/experience/": {
    get: {
      req: ApiExperienceListData;
      res: {
        200: PaginatedExperienceList;
      };
    };
    post: {
      req: ApiExperienceCreateData;
      res: {
        201: Experience;
      };
    };
  };
  "/api/experience/{id}/": {
    get: {
      req: ApiExperienceRetrieveData;
      res: {
        200: Experience;
      };
    };
    put: {
      req: ApiExperienceUpdateData;
      res: {
        200: Experience;
      };
    };
    patch: {
      req: ApiExperiencePartialUpdateData;
      res: {
        200: Experience;
      };
    };
    delete: {
      req: ApiExperienceDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/googlelogin/": {
    post: {
      req: ApiGoogleloginCreateData;
      res: {
        200: GoogleAuth;
      };
    };
  };
  "/api/job-skill-matrices/": {
    get: {
      req: ApiJobSkillMatricesListData;
      res: {
        200: PaginatedJobSkillMatrixList;
      };
    };
    post: {
      req: ApiJobSkillMatricesCreateData;
      res: {
        201: JobSkillMatrix;
      };
    };
  };
  "/api/job-skill-matrices/{id}/": {
    get: {
      req: ApiJobSkillMatricesRetrieveData;
      res: {
        200: JobSkillMatrix;
      };
    };
    put: {
      req: ApiJobSkillMatricesUpdateData;
      res: {
        200: JobSkillMatrix;
      };
    };
    patch: {
      req: ApiJobSkillMatricesPartialUpdateData;
      res: {
        200: JobSkillMatrix;
      };
    };
    delete: {
      req: ApiJobSkillMatricesDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/job-skill-matrices/{id}/update_skill/": {
    post: {
      req: ApiJobSkillMatricesUpdateSkillCreateData;
      res: {
        200: JobSkillMatrix;
      };
    };
  };
  "/api/jobprofile/": {
    get: {
      req: ApiJobprofileListData;
      res: {
        200: PaginatedJobProfileList;
      };
    };
    post: {
      req: ApiJobprofileCreateData;
      res: {
        201: JobProfile;
      };
    };
  };
  "/api/jobprofile/{id}/": {
    get: {
      req: ApiJobprofileRetrieveData;
      res: {
        200: JobProfile;
      };
    };
    put: {
      req: ApiJobprofileUpdateData;
      res: {
        200: JobProfile;
      };
    };
    patch: {
      req: ApiJobprofilePartialUpdateData;
      res: {
        200: JobProfile;
      };
    };
    delete: {
      req: ApiJobprofileDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/league/commissioner/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/commissioner/ledger/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/commissioner/roster/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/commissioner/timeline/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/free-agents/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/free-agents/me/": {
    get: {
      res: {
        200: FreeAgent;
      };
    };
    put: {
      res: {
        200: FreeAgent;
      };
    };
  };
  "/api/league/judging/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
    post: {
      res: {
        /**
         * No response body
         */
        201: unknown;
      };
    };
  };
  "/api/league/judging/finalize/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/leaderboard/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/leaderboard/chapters/": {
    get: {
      req: ApiLeagueLeaderboardChaptersListData;
      res: {
        200: Array<ChapterLeaderboard>;
      };
    };
  };
  "/api/league/leaderboard/developers/": {
    get: {
      req: ApiLeagueLeaderboardDevelopersListData;
      res: {
        200: Array<DeveloperLeaderboard>;
      };
    };
  };
  "/api/league/leaderboard/teams/": {
    get: {
      req: ApiLeagueLeaderboardTeamsListData;
      res: {
        200: Array<TeamLeaderboard>;
      };
    };
  };
  "/api/league/mode/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/mode/switch/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/my-challenges/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/my-score/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/notifications/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/notifications/read_all/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/organizer/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/phases/{id}/": {
    get: {
      req: ApiLeaguePhasesRetrieveData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/phases/{id}/bracket/": {
    post: {
      req: ApiLeaguePhasesBracketCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/phases/{id}/freeze/": {
    post: {
      req: ApiLeaguePhasesFreezeCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/recruit/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/recruit/co_membership/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/roster-invites/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
    post: {
      res: {
        /**
         * No response body
         */
        201: unknown;
      };
    };
  };
  "/api/league/roster-invites/{id}/approve/": {
    post: {
      req: ApiLeagueRosterInvitesApproveCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/roster-invites/{id}/confirm/": {
    post: {
      req: ApiLeagueRosterInvitesConfirmCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/roster-invites/{id}/decline/": {
    post: {
      req: ApiLeagueRosterInvitesDeclineCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/roster-invites/{id}/lock/": {
    post: {
      req: ApiLeagueRosterInvitesLockCreateData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/seasons/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/seasons/{id}/": {
    get: {
      req: ApiLeagueSeasonsRetrieve2Data;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/league/solo-team/": {
    post: {
      res: {
        /**
         * No response body
         */
        201: unknown;
      };
    };
  };
  "/api/league/submissions/": {
    get: {
      res: {
        200: Array<Submission>;
      };
    };
    post: {
      req: ApiLeagueSubmissionsCreateData;
      res: {
        201: Submission;
      };
    };
  };
  "/api/league/submissions/{id}/": {
    get: {
      req: ApiLeagueSubmissionsRetrieveData;
      res: {
        200: Submission;
      };
    };
    put: {
      req: ApiLeagueSubmissionsUpdateData;
      res: {
        200: Submission;
      };
    };
    patch: {
      req: ApiLeagueSubmissionsPartialUpdateData;
      res: {
        200: Submission;
      };
    };
    delete: {
      req: ApiLeagueSubmissionsDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/league/submissions/{id}/lock/": {
    post: {
      req: ApiLeagueSubmissionsLockCreateData;
      res: {
        200: Submission;
      };
    };
  };
  "/api/league/submissions/{id}/validate/": {
    post: {
      req: ApiLeagueSubmissionsValidateCreateData;
      res: {
        200: Submission;
      };
    };
  };
  "/api/league/submissions/options/": {
    get: {
      res: {
        200: Submission;
      };
    };
  };
  "/api/league/team-matches/{id}/": {
    get: {
      req: ApiLeagueTeamMatchesRetrieveData;
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/login/": {
    post: {
      req: ApiLoginCreateData;
      res: {
        200: Login;
      };
    };
  };
  "/api/logout/": {
    post: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/match-results/": {
    get: {
      req: ApiMatchResultsListData;
      res: {
        200: PaginatedMatchResultList;
      };
    };
  };
  "/api/match-results/{id}/": {
    get: {
      req: ApiMatchResultsRetrieveData;
      res: {
        200: MatchResult;
      };
    };
    put: {
      req: ApiMatchResultsUpdateData;
      res: {
        200: MatchResult;
      };
    };
    patch: {
      req: ApiMatchResultsPartialUpdateData;
      res: {
        200: MatchResult;
      };
    };
  };
  "/api/match-results/save-job/": {
    post: {
      req: ApiMatchResultsSaveJobCreateData;
      res: {
        200: MatchResult;
      };
    };
  };
  "/api/matching-sessions/": {
    get: {
      req: ApiMatchingSessionsListData;
      res: {
        200: PaginatedMatchingSessionList;
      };
    };
  };
  "/api/matching-sessions/{id}/": {
    get: {
      req: ApiMatchingSessionsRetrieveData;
      res: {
        200: MatchingSession;
      };
    };
  };
  "/api/matching-skills/": {
    get: {
      req: ApiMatchingSkillsListData;
      res: {
        200: PaginatedSkillList;
      };
    };
  };
  "/api/matching-skills/{id}/": {
    get: {
      req: ApiMatchingSkillsRetrieveData;
      res: {
        200: Skill;
      };
    };
  };
  "/api/matching-skills/autocomplete/": {
    get: {
      res: {
        200: Skill;
      };
    };
  };
  "/api/matching/analytics/": {
    get: {
      res: {
        200: MatchingSession;
      };
    };
  };
  "/api/matching/extract-skills/": {
    post: {
      req: ApiMatchingExtractSkillsCreateData;
      res: {
        200: Skill;
      };
    };
  };
  "/api/matching/match/": {
    get: {
      res: {
        200: MatchResult;
      };
    };
    post: {
      req: ApiMatchingMatchCreateData;
      res: {
        200: MatchResult;
      };
    };
  };
  "/api/matching/task-status/": {
    get: {
      res: {
        /**
         * No response body
         */
        200: unknown;
      };
    };
  };
  "/api/nonprofit-profile/": {
    get: {
      req: ApiNonprofitProfileListData;
      res: {
        200: PaginatedNonProfitProfileList;
      };
    };
    post: {
      req: ApiNonprofitProfileCreateData;
      res: {
        201: NonProfitProfile;
      };
    };
  };
  "/api/nonprofit-profile/{id}/": {
    get: {
      req: ApiNonprofitProfileRetrieveData;
      res: {
        200: NonProfitProfile;
      };
    };
    put: {
      req: ApiNonprofitProfileUpdateData;
      res: {
        200: NonProfitProfile;
      };
    };
    patch: {
      req: ApiNonprofitProfilePartialUpdateData;
      res: {
        200: NonProfitProfile;
      };
    };
    delete: {
      req: ApiNonprofitProfileDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/profile/": {
    get: {
      req: ApiProfileListData;
      res: {
        200: PaginatedProfileList;
      };
    };
    post: {
      req: ApiProfileCreateData;
      res: {
        201: Profile;
      };
    };
  };
  "/api/profile/{id}/": {
    get: {
      req: ApiProfileRetrieveData;
      res: {
        200: Profile;
      };
    };
    put: {
      req: ApiProfileUpdateData;
      res: {
        200: Profile;
      };
    };
    patch: {
      req: ApiProfilePartialUpdateData;
      res: {
        200: Profile;
      };
    };
    delete: {
      req: ApiProfileDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/project/": {
    get: {
      req: ApiProjectListData;
      res: {
        200: PaginatedProjectList;
      };
    };
    post: {
      req: ApiProjectCreateData;
      res: {
        201: Project;
      };
    };
  };
  "/api/project/{id}/": {
    get: {
      req: ApiProjectRetrieveData;
      res: {
        200: Project;
      };
    };
    put: {
      req: ApiProjectUpdateData;
      res: {
        200: Project;
      };
    };
    patch: {
      req: ApiProjectPartialUpdateData;
      res: {
        200: Project;
      };
    };
    delete: {
      req: ApiProjectDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/publication/": {
    get: {
      req: ApiPublicationListData;
      res: {
        200: PaginatedPublicationList;
      };
    };
    post: {
      req: ApiPublicationCreateData;
      res: {
        201: Publication;
      };
    };
  };
  "/api/publication/{id}/": {
    get: {
      req: ApiPublicationRetrieveData;
      res: {
        200: Publication;
      };
    };
    put: {
      req: ApiPublicationUpdateData;
      res: {
        200: Publication;
      };
    };
    patch: {
      req: ApiPublicationPartialUpdateData;
      res: {
        200: Publication;
      };
    };
    delete: {
      req: ApiPublicationDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/resources/": {
    get: {
      req: ApiResourcesListData;
      res: {
        200: PaginatedResourceListList;
      };
    };
    post: {
      req: ApiResourcesCreateData;
      res: {
        201: Resource;
      };
    };
  };
  "/api/resources/{id}/": {
    get: {
      req: ApiResourcesRetrieveData;
      res: {
        200: Resource;
      };
    };
    put: {
      req: ApiResourcesUpdateData;
      res: {
        200: Resource;
      };
    };
    patch: {
      req: ApiResourcesPartialUpdateData;
      res: {
        200: Resource;
      };
    };
    delete: {
      req: ApiResourcesDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/resources/{id}/increment_downloads/": {
    post: {
      req: ApiResourcesIncrementDownloadsCreateData;
      res: {
        200: Resource;
      };
    };
  };
  "/api/resources/{id}/increment_views/": {
    post: {
      req: ApiResourcesIncrementViewsCreateData;
      res: {
        200: Resource;
      };
    };
  };
  "/api/resources/free/": {
    get: {
      res: {
        200: Resource;
      };
    };
  };
  "/api/resources/popular/": {
    get: {
      res: {
        200: Resource;
      };
    };
  };
  "/api/rest/rest-check/": {
    get: {
      res: {
        200: Message;
      };
    };
  };
  "/api/session/": {
    get: {
      res: {
        200: Session;
      };
    };
  };
  "/api/signup/": {
    post: {
      req: ApiSignupCreateData;
      res: {
        200: Signup;
      };
    };
  };
  "/api/skill/": {
    get: {
      req: ApiSkillListData;
      res: {
        200: PaginatedApplicationSkillList;
      };
    };
    post: {
      req: ApiSkillCreateData;
      res: {
        201: ApplicationSkill;
      };
    };
  };
  "/api/skill-categories/": {
    get: {
      req: ApiSkillCategoriesListData;
      res: {
        200: PaginatedSkillCategoryList;
      };
    };
  };
  "/api/skill-categories/{id}/": {
    get: {
      req: ApiSkillCategoriesRetrieveData;
      res: {
        200: SkillCategory;
      };
    };
  };
  "/api/skill/{id}/": {
    get: {
      req: ApiSkillRetrieveData;
      res: {
        200: ApplicationSkill;
      };
    };
    put: {
      req: ApiSkillUpdateData;
      res: {
        200: ApplicationSkill;
      };
    };
    patch: {
      req: ApiSkillPartialUpdateData;
      res: {
        200: ApplicationSkill;
      };
    };
    delete: {
      req: ApiSkillDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/smallbiz-profile/": {
    get: {
      req: ApiSmallbizProfileListData;
      res: {
        200: PaginatedSmallBusinessProfileList;
      };
    };
    post: {
      req: ApiSmallbizProfileCreateData;
      res: {
        201: SmallBusinessProfile;
      };
    };
  };
  "/api/smallbiz-profile/{id}/": {
    get: {
      req: ApiSmallbizProfileRetrieveData;
      res: {
        200: SmallBusinessProfile;
      };
    };
    put: {
      req: ApiSmallbizProfileUpdateData;
      res: {
        200: SmallBusinessProfile;
      };
    };
    patch: {
      req: ApiSmallbizProfilePartialUpdateData;
      res: {
        200: SmallBusinessProfile;
      };
    };
    delete: {
      req: ApiSmallbizProfileDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/tags/": {
    get: {
      req: ApiTagsListData;
      res: {
        200: PaginatedTagList;
      };
    };
  };
  "/api/tags/{id}/": {
    get: {
      req: ApiTagsRetrieveData;
      res: {
        200: Tag;
      };
    };
  };
  "/api/tags/popular/": {
    get: {
      res: {
        200: Tag;
      };
    };
  };
  "/api/user-skill-matrices/": {
    get: {
      req: ApiUserSkillMatricesListData;
      res: {
        200: PaginatedUserSkillMatrixList;
      };
    };
    post: {
      req: ApiUserSkillMatricesCreateData;
      res: {
        201: UserSkillMatrix;
      };
    };
  };
  "/api/user-skill-matrices/{id}/": {
    get: {
      req: ApiUserSkillMatricesRetrieveData;
      res: {
        200: UserSkillMatrix;
      };
    };
    put: {
      req: ApiUserSkillMatricesUpdateData;
      res: {
        200: UserSkillMatrix;
      };
    };
    patch: {
      req: ApiUserSkillMatricesPartialUpdateData;
      res: {
        200: UserSkillMatrix;
      };
    };
    delete: {
      req: ApiUserSkillMatricesDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/user-skill-matrices/{id}/update_skill/": {
    post: {
      req: ApiUserSkillMatricesUpdateSkillCreateData;
      res: {
        200: UserSkillMatrix;
      };
    };
  };
  "/api/users/": {
    get: {
      req: ApiUsersListData;
      res: {
        200: PaginatedUserList;
      };
    };
    post: {
      req: ApiUsersCreateData;
      res: {
        201: User;
      };
    };
  };
  "/api/users/{id}/": {
    get: {
      req: ApiUsersRetrieveData;
      res: {
        200: User;
      };
    };
    put: {
      req: ApiUsersUpdateData;
      res: {
        200: User;
      };
    };
    patch: {
      req: ApiUsersPartialUpdateData;
      res: {
        200: User;
      };
    };
    delete: {
      req: ApiUsersDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/users/dashboard/job-search-stats/": {
    get: {
      req: ApiUsersDashboardJobSearchStatsRetrieveData;
      res: {
        200: {
          applications?: number;
          job_matches?: number;
          screens?: number;
          offers?: number;
        };
      };
    };
  };
  "/api/users/dashboard/profile-completion/": {
    get: {
      res: {
        200: {
          completion?: number;
          checklist?: Array<{
            label?: string;
            done?: boolean;
          }>;
        };
      };
    };
  };
  "/api/users/dashboard/recent-activity/": {
    get: {
      res: {
        200: {
          activities?: Array<{
            id?: number;
            type?: string;
            company?: string;
            position?: string;
            date?: string;
            status?: string;
          }>;
        };
      };
    };
  };
  "/api/volunteer-opportunities/": {
    get: {
      req: ApiVolunteerOpportunitiesListData;
      res: {
        200: PaginatedVolunteerOpportunityList;
      };
    };
    post: {
      req: ApiVolunteerOpportunitiesCreateData;
      res: {
        201: VolunteerOpportunity;
      };
    };
  };
  "/api/volunteer-opportunities/{id}/": {
    get: {
      req: ApiVolunteerOpportunitiesRetrieveData;
      res: {
        200: VolunteerOpportunity;
      };
    };
    put: {
      req: ApiVolunteerOpportunitiesUpdateData;
      res: {
        200: VolunteerOpportunity;
      };
    };
    patch: {
      req: ApiVolunteerOpportunitiesPartialUpdateData;
      res: {
        200: VolunteerOpportunity;
      };
    };
    delete: {
      req: ApiVolunteerOpportunitiesDestroyData;
      res: {
        /**
         * No response body
         */
        204: void;
      };
    };
  };
  "/api/volunteer-opportunities/my_opportunities/": {
    get: {
      res: {
        200: VolunteerOpportunity;
      };
    };
  };
  "/api/whoami/": {
    get: {
      res: {
        200: WhoAmI;
      };
    };
  };
  "/api/candidate-profile/{candidate_id}/": {
    get: {
      req: ApiCandidateProfileRetrieveData;
      res: {
        200: CandidatePublicProfile;
        /**
         * Access denied - only recruiters, staff, and admins can view candidate profiles
         */
        403: unknown;
        /**
         * Candidate not found or not looking for jobs
         */
        404: unknown;
      };
    };
  };
  "/api/search/candidates/": {
    get: {
      req: ApiSearchCandidatesRetrieveData;
      res: {
        200: SearchResponse;
        /**
         * Invalid request parameters
         */
        400: unknown;
        /**
         * Access denied
         */
        403: unknown;
        /**
         * Internal server error
         */
        500: unknown;
      };
    };
    post: {
      req: ApiSearchCandidatesCreateData;
      res: {
        200: SearchResponse;
        /**
         * Invalid request data
         */
        400: unknown;
        /**
         * Access denied - only recruiters can search candidates
         */
        403: unknown;
        /**
         * Internal server error
         */
        500: unknown;
      };
    };
  };
  "/api/search/suggestions/": {
    get: {
      req: ApiSearchSuggestionsRetrieveData;
      res: {
        /**
         * Search suggestions
         */
        200: unknown;
        /**
         * Invalid request
         */
        400: unknown;
        /**
         * Access denied
         */
        403: unknown;
      };
    };
  };
  "/api/send-email/": {
    post: {
      req: ApiSendEmailCreateData;
      res: {
        /**
         * Email sent successfully.
         */
        200: unknown;
        /**
         * Validation error.
         */
        400: unknown;
        /**
         * Failed to send email.
         */
        500: unknown;
      };
    };
  };
  "/common/rest-check/": {
    get: {
      res: {
        200: Message;
      };
    };
  };
  "/common/resume/auto-prefill/{task_id}/": {
    get: {
      req: CommonResumeAutoPrefillRetrieveData;
      res: {
        200: Message;
      };
    };
  };
  "/common/resume/enhanced/": {
    post: {
      req: CommonResumeEnhancedCreateData;
      res: {
        200: Message;
      };
    };
  };
  "/common/resume/result/{task_id}/": {
    get: {
      req: CommonResumeResultRetrieveData;
      res: {
        200: Message;
      };
    };
  };
  "/common/resume/upload/": {
    get: {
      res: {
        /**
         * Resume information retrieved successfully
         */
        200: unknown;
        /**
         * User not authenticated
         */
        401: unknown;
        /**
         * No resume found for user
         */
        404: unknown;
      };
    };
    post: {
      req: CommonResumeUploadCreateData;
      res: {
        /**
         * Resume uploaded and processing started
         */
        202: unknown;
        /**
         * No file uploaded
         */
        400: unknown;
        /**
         * User not authenticated
         */
        401: unknown;
      };
    };
    put: {
      req: CommonResumeUploadUpdateData;
      res: {
        /**
         * Resume replaced and processing started
         */
        200: unknown;
        /**
         * No file uploaded
         */
        400: unknown;
        /**
         * User not authenticated
         */
        401: unknown;
      };
    };
    delete: {
      res: {
        /**
         * Resume deleted successfully
         */
        200: unknown;
        /**
         * User not authenticated
         */
        401: unknown;
        /**
         * No resume found for user
         */
        404: unknown;
      };
    };
  };
};
